import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { businessUnitsApi } from "../../api/businessUnitsApi";
import type { BusinessUnit } from "../../types/businessUnit.types";
import type { RootState, AppDispatch } from "../../app/store";
import { toast } from "../../components/common/toast";
import { getApiErrorMessage } from "../../utils/apiError";
import { withRecordMedia } from "../../utils/recordMedia";
import { reportDeleteFailure } from "../../utils/deleteFailure";
import i18n from "../../i18n";
import { getCurrentUser } from "../../auth/auth.service";

interface BusinessUnitsState {
  items: BusinessUnit[];
  isLoading: boolean;
  isAdding: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  hasMore: boolean;
  pageIndex: number;
  totalCount: number;
  error: string | null;
}

const initialState: BusinessUnitsState = {
  items: [],
  isLoading: false,
  isAdding: false,
  isEditing: false,
  isDeleting: false,
  hasMore: true,
  pageIndex: 0,
  totalCount: 0,
  error: null,
};

const businessUnitsSlice = createSlice({
  name: "businessUnits",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setAdding: (state, action: PayloadAction<boolean>) => {
      state.isAdding = action.payload;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.isDeleting = action.payload;
    },
    setBusinessUnits: (state, action: PayloadAction<BusinessUnit[]>) => {
      state.items = action.payload;
    },
    appendBusinessUnits: (state, action: PayloadAction<BusinessUnit[]>) => {
      state.items = [...state.items, ...action.payload];
    },

    addSingleUnitLocally: (state, action: PayloadAction<BusinessUnit>) => {
      state.items.unshift(action.payload); // Top par add karega
      state.totalCount += 1;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    resetBusinessUnitList: (state) => {
      state.items = [];
      state.hasMore = true;
      state.pageIndex = 0;
      state.totalCount = 0;
    },

    updateSingleUnit: (
      state,
      action: PayloadAction<Partial<BusinessUnit> & { id: string }>,
    ) => {
      const index = state.items.findIndex((u) => u.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },

    removeSingleUnit: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((u) => u.id !== action.payload);
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
  },
});

export const {
  setLoading,
  setAdding,
  setEditing,
  setDeleting,
  setBusinessUnits,
  appendBusinessUnits,
  addSingleUnitLocally,
  setTotalCount,
  setHasMore,
  setPageIndex,
  resetBusinessUnitList,
  updateSingleUnit,
  removeSingleUnit,
} = businessUnitsSlice.actions;

// The newest first-page request (see fetchBusinessUnits).
let latestBusinessUnitsPage = 0;

export const fetchBusinessUnits = (payload: any = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().businessUnits;
    const userData = await getCurrentUser();

    const incomingPageIndex = payload.hasOwnProperty("pageIndex")
      ? payload.pageIndex
      : state.pageIndex;

    // A first page always runs (a new search must not be dropped because an
    // earlier one is still loading); an answer to an older one is ignored.
    const isFirstPage = incomingPageIndex === 0;
    if ((state.isLoading || !state.hasMore) && !isFirstPage) return;
    const ticket = isFirstPage
      ? ++latestBusinessUnitsPage
      : latestBusinessUnitsPage;
    const superseded = () => ticket !== latestBusinessUnitsPage;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "string",
        // "Sort by" from the listing (resolveSort gives sortAscending; this
        // endpoint takes the opposite flag).
        sortDecending: !(payload.sortAscending ?? false),
        // Always scoped to the current (switched) organisation — never let a
        // caller-supplied filter override the tenant boundary.
        groupIds: [`${userData.companyId}`],
        ids: payload.ids || [],
        isActive: payload.isActive ?? null,
        codes: payload.codes || [],
        cities: payload.cities || [],
        states: payload.states || [],
        searchText: payload.searchText || "",
        // A lookup by id (a detail page, often opened fresh from the URL)
        // must find the record whatever its state: without this a deleted
        // record's page loaded once from the list and then came back empty
        // on reload, because the search defaults to excluding deleted rows.
        includeDeleted: payload.includeDeleted ?? !!payload.ids?.length,
      };

      const response = await businessUnitsApi.searchBusinessUnits(requestBody);
      // Pictures and files are stored as ids; resolve their links for the
      // whole page in one request (utils/recordMedia).
      // Photo links are signed only for a page someone looks at (a listing
      // page or a lookup by id). A 100-500 row fetch that fills a filter
      // dropdown shows no photos, and signing them cost a second request.
      const withMedia =
        (payload as { withMedia?: boolean }).withMedia ??
        ((payload.pageSize ?? 10) <= 50 || !!payload.ids?.length);
      const fetchedData = withMedia
        ? await withRecordMedia<any>(response.data || [])
        : response.data || [];
      const totalCount = response.count || response.totalCount || 0;

      if (superseded()) return;
      dispatch(setTotalCount(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.items.length + fetchedData.length;

      if (superseded()) return;
      if (currentLoadedCount >= totalCount || fetchedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setBusinessUnits(fetchedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendBusinessUnits(fetchedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Business Units error:", error);
      toast.error(
        i18n.t("common:slices.businessUnits.fetchError", {
          defaultValue: "Failed to fetch business units",
        }),
      );
    } finally {
      if (!superseded()) dispatch(setLoading(false));
    }
  };
};

export const addBusinessUnit = (data: any) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setAdding(true));
      const res = await businessUnitsApi.createBusinessUnit(data);

      const createdId = res?.data?.id || res?.data || res?.id || res;

      const newBu: BusinessUnit = {
        ...(data as BusinessUnit),
        id: createdId,
        isActive: true,
      };

      dispatch(addSingleUnitLocally(newBu));

      toast.success(
        i18n.t("common:slices.businessUnits.saveSuccess", {
          defaultValue: "Business unit created successfully",
        }),
      );
      return newBu;
    } catch (error) {
      console.error("Create Business Unit error:", error);
      toast.error(
        getApiErrorMessage(
          error,
          i18n.t("common:slices.businessUnits.saveError", {
            defaultValue: "Failed to create business unit",
          }),
        ),
      );
      throw error;
    } finally {
      dispatch(setAdding(false));
    }
  };
};
export const editBusinessUnit = (data: any) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setEditing(true));
      // An edit replaces the whole record, and the edit forms know nothing
      // of the picture or the files — carry them over unless the caller is
      // the one changing them.
      const existing: any = getState().businessUnits.items.find(
        (item: any) => item.id === data.id,
      );
      const payload = {
        profilePic: existing?.profilePic,
        mediaJson: existing?.mediaJson,
        ...data,
      };
      const res = await businessUnitsApi.updateBusinessUnit(payload);
      const updatedUnit = res?.data || payload;

      dispatch(updateSingleUnit(updatedUnit));

      toast.success(
        i18n.t("common:slices.businessUnits.updateSuccess", {
          defaultValue: "Business unit updated successfully",
        }),
      );
      return updatedUnit;
    } catch (error) {
      console.error("Edit Business Unit error:", error);
      toast.error(
        getApiErrorMessage(
          error,
          i18n.t("common:slices.businessUnits.paramUpdateError", {
            defaultValue: "Failed to update business unit",
          }),
        ),
      );
      throw error;
    } finally {
      dispatch(setEditing(false));
    }
  };
};

export const activateBusinessUnit = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(updateSingleUnit({ id, isActive: true }));

      await businessUnitsApi.activateBusinessUnit(id);

      toast.success(
        i18n.t("common:slices.businessUnits.activateSuccess", {
          defaultValue: "Business unit activated",
        }),
      );
    } catch (error) {
      dispatch(updateSingleUnit({ id, isActive: false }));
      console.error("Activate Business Unit error:", error);
      toast.error(
        i18n.t("common:slices.businessUnits.activateError", {
          defaultValue: "Failed to activate business unit",
        }),
      );
    }
  };
};

export const deactivateBusinessUnit = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(updateSingleUnit({ id, isActive: false }));

      await businessUnitsApi.deActivateBusinessUnit(id);

      toast.success(
        i18n.t("common:slices.businessUnits.deactivateSuccess", {
          defaultValue: "Business unit deactivated",
        }),
      );
    } catch (error) {
      dispatch(updateSingleUnit({ id, isActive: true }));
      console.error("Deactivate Business Unit error:", error);
      toast.error(
        i18n.t("common:slices.businessUnits.deactivateError", {
          defaultValue: "Failed to deactivate business unit",
        }),
      );
    }
  };
};

export const removeBusinessUnit = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setDeleting(true));

      dispatch(removeSingleUnit(id));

      await businessUnitsApi.removeBusinessUnit(id);

      toast.success(
        i18n.t("common:slices.businessUnits.deleteSuccess", {
          defaultValue: "Business unit removed successfully",
        }),
      );
    } catch (error) {
      console.error("Remove Business Unit error:", error);
      // The API refuses deletion while contracts or staff still use the
      // record and says which — show that reason, not a generic failure.
      const fallback = i18n.t("common:slices.businessUnits.deleteError", {
        defaultValue: "Failed to remove business unit",
      });
      reportDeleteFailure(error, fallback);
      throw error;
    } finally {
      dispatch(setDeleting(false));
    }
  };
};

export default businessUnitsSlice.reducer;
